"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTypeormEntity = exports.getTypeormEntityProperties = exports.EntityRelationDescription = exports.EntityColumnDescription = exports.EmbeddedEntityDescription = exports.EntityDescription = void 0;
const typeorm_1 = require("typeorm/browser");
const types_1 = require("./types");
function constructorToSimpleType(constructor) {
    if (constructor === Number) {
        return 'double';
    }
    else {
        return constructor.name.toLowerCase();
    }
}
class EntityDescription extends types_1.AbstractEntityDescription {
    constructor(entityMetadata) {
        super();
        this.entityMetadata = entityMetadata;
    }
    get type() {
        if (this.entityMetadata.tableType === 'view') {
            return 'view';
        }
        return 'regular';
    }
}
exports.EntityDescription = EntityDescription;
class EmbeddedEntityDescription extends types_1.AbstractEntityEmbeddedDescription {
    constructor(columMetadata) {
        super();
        this._propertyName = columMetadata.parentPropertyNames.join(',');
        this._columns = columMetadata.columns.map((c) => new EntityColumnDescription(c));
        this._type = columMetadata.type;
    }
    get propertyName() {
        return this._propertyName;
    }
    get columns() {
        return this._columns;
    }
    get type() {
        return this._type;
    }
}
exports.EmbeddedEntityDescription = EmbeddedEntityDescription;
class EntityColumnDescription extends types_1.AbstractEntityColumnDescription {
    constructor(columnMetadata) {
        super();
        this.columnMetadata = columnMetadata;
    }
    get databaseName() {
        return this.columnMetadata.databaseName;
    }
    get propertyName() {
        return this.columnMetadata.propertyName;
    }
    get propertyPath() {
        var _a;
        return ((_a = this.columnMetadata.embeddedMetadata) === null || _a === void 0 ? void 0 : _a.propertyPath)
            ? `${this.columnMetadata.embeddedMetadata.propertyPath}.${this.propertyName}`
            : this.propertyName;
    }
    get isReadOnly() {
        return (this.isGenerated ||
            this.isCreateDate ||
            this.isUpdateDate ||
            this.isDeleteDate);
    }
    get isGenerated() {
        return this.columnMetadata.isGenerated;
    }
    get isCreateDate() {
        return this.columnMetadata.isCreateDate;
    }
    get isUpdateDate() {
        return this.columnMetadata.isUpdateDate;
    }
    get isDeleteDate() {
        return this.columnMetadata.isDeleteDate;
    }
    get isNullable() {
        return this.columnMetadata.isNullable;
    }
    get hasDefaultValue() {
        return (this.columnMetadata.default !== undefined ||
            this.columnMetadata.isGenerated);
    }
    get defaultValue() {
        return typeof this.columnMetadata.default !== 'function'
            ? this.columnMetadata.default
            : undefined;
    }
    get isPrimary() {
        return this.columnMetadata.isPrimary;
    }
    get isArray() {
        return this.columnMetadata.isArray;
    }
    get columnType() {
        var _a, _b;
        let type = this.columnMetadata.type;
        if (typeof type === 'function') {
            type = constructorToSimpleType(type);
        }
        if (!type) {
            type = (_b = (_a = this.columnMetadata.embeddedMetadata) === null || _a === void 0 ? void 0 : _a.type) === null || _b === void 0 ? void 0 : _b.toString();
        }
        if (!type) {
            const reflectMetadataType = Reflect.getMetadata('design:type', this.columnMetadata.target.prototype, this.propertyName);
            if (reflectMetadataType) {
                type = constructorToSimpleType(reflectMetadataType);
            }
        }
        if (!type) {
            return;
        }
        if (EntityColumnDescription.integerColumnTypes.has(type)) {
            return {
                type: types_1.SimplifiedColumnTypeEnum.INTEGER,
            };
        }
        if (EntityColumnDescription.floatColumnTypes.has(type)) {
            return {
                type: types_1.SimplifiedColumnTypeEnum.FLOAT,
            };
        }
        if (EntityColumnDescription.precisionColumnTypes.has(type)) {
            return {
                type: types_1.SimplifiedColumnTypeEnum.PRECISION,
                precision: this.columnMetadata.precision,
                scale: this.columnMetadata.scale,
            };
        }
        if (EntityColumnDescription.textColumnTypes.has(type)) {
            return {
                type: types_1.SimplifiedColumnTypeEnum.TEXT,
                length: parseInt(this.columnMetadata.length) || null,
            };
        }
        if (EntityColumnDescription.jsonColumnTypes.has(type)) {
            return {
                type: types_1.SimplifiedColumnTypeEnum.JSON,
            };
        }
        if (type === 'boolean' || type === 'bool') {
            return {
                type: types_1.SimplifiedColumnTypeEnum.BOOLEAN,
            };
        }
        if (type === 'enum' || type === 'simple-enum') {
            return {
                type: types_1.SimplifiedColumnTypeEnum.ENUM,
            };
        }
        if (type === 'datetime' || type === 'date') {
            return {
                type: types_1.SimplifiedColumnTypeEnum.DATETIME,
            };
        }
        if (type === 'uuid') {
            return {
                type: types_1.SimplifiedColumnTypeEnum.UUID,
            };
        }
        return {
            type: types_1.SimplifiedColumnTypeEnum.UNKNOWN,
        };
    }
    get enumValues() {
        return this.columnMetadata.enum;
    }
    get isRelation() {
        return !!this.columnMetadata.relationMetadata;
    }
    get isRelationOwner() {
        var _a;
        return !!((_a = this.columnMetadata.relationMetadata) === null || _a === void 0 ? void 0 : _a.isOwning);
    }
    get relationType() {
        var _a, _b, _c, _d;
        if ((_a = this.columnMetadata.relationMetadata) === null || _a === void 0 ? void 0 : _a.isOneToOne) {
            return types_1.SimplifiedRelationType.ONE_TO_ONE;
        }
        if ((_b = this.columnMetadata.relationMetadata) === null || _b === void 0 ? void 0 : _b.isOneToMany) {
            return types_1.SimplifiedRelationType.ONE_TO_MANY;
        }
        if ((_c = this.columnMetadata.relationMetadata) === null || _c === void 0 ? void 0 : _c.isManyToOne) {
            return types_1.SimplifiedRelationType.MANY_TO_ONE;
        }
        if ((_d = this.columnMetadata.relationMetadata) === null || _d === void 0 ? void 0 : _d.isManyToMany) {
            return types_1.SimplifiedRelationType.MANY_TO_MANY;
        }
    }
    get relationReferencedColumn() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.columnMetadata.relationMetadata) === null || _a === void 0 ? void 0 : _a.joinColumns.find((jc) => { var _a; return jc.target === ((_a = this.columnMetadata.relationMetadata) === null || _a === void 0 ? void 0 : _a.target); })) === null || _b === void 0 ? void 0 : _b.referencedColumn) === null || _c === void 0 ? void 0 : _c.propertyPath;
    }
    get relationEntity() {
        var _a, _b;
        return (_b = (_a = this.columnMetadata.relationMetadata) === null || _a === void 0 ? void 0 : _a.inverseEntityMetadata) === null || _b === void 0 ? void 0 : _b.target;
    }
    get isEagerRelation() {
        var _a;
        return (_a = this.columnMetadata.relationMetadata) === null || _a === void 0 ? void 0 : _a.isEager;
    }
    get relationInverseSidePropertyPath() {
        var _a;
        return (_a = this.columnMetadata.relationMetadata) === null || _a === void 0 ? void 0 : _a.inverseSidePropertyPath;
    }
    get isCascadeRelation() {
        return this.cascadeRelations !== undefined;
    }
    get isEmbed() {
        return !!this.columnMetadata.embeddedMetadata;
    }
    get cascadeRelations() {
        var _a, _b, _c, _d, _e;
        const result = {
            insert: (_a = this.columnMetadata.relationMetadata) === null || _a === void 0 ? void 0 : _a.isCascadeInsert,
            update: (_b = this.columnMetadata.relationMetadata) === null || _b === void 0 ? void 0 : _b.isCascadeUpdate,
            remove: (_c = this.columnMetadata.relationMetadata) === null || _c === void 0 ? void 0 : _c.isCascadeRemove,
            softRemove: (_d = this.columnMetadata.relationMetadata) === null || _d === void 0 ? void 0 : _d.isCascadeSoftRemove,
            recover: (_e = this.columnMetadata.relationMetadata) === null || _e === void 0 ? void 0 : _e.isCascadeSoftRemove,
        };
        if (Object.values(result).every((flag) => !flag)) {
            return undefined;
        }
        return result;
    }
    get embeds() {
        if (!this.columnMetadata.embeddedMetadata) {
            return;
        }
        return new EmbeddedEntityDescription(this.columnMetadata.embeddedMetadata);
    }
}
exports.EntityColumnDescription = EntityColumnDescription;
class EntityRelationDescription extends types_1.AbstractEntityColumnDescription {
    constructor(relationMetadata) {
        super();
        this.relationMetadata = relationMetadata;
    }
    get databaseName() {
        throw new Error('Method not implemented.');
    }
    get propertyName() {
        return this.relationMetadata.propertyName;
    }
    get propertyPath() {
        return this.propertyName;
    }
    get isReadOnly() {
        return false;
    }
    get isGenerated() {
        return false;
    }
    get isCreateDate() {
        return false;
    }
    get isUpdateDate() {
        return false;
    }
    get isDeleteDate() {
        return false;
    }
    get isNullable() {
        return this.relationMetadata.isNullable;
    }
    get hasDefaultValue() {
        return false;
    }
    get defaultValue() {
        return undefined;
    }
    get isPrimary() {
        return false;
    }
    get isArray() {
        return false;
    }
    get columnType() {
        return {
            type: types_1.SimplifiedColumnTypeEnum.RELATION,
        };
    }
    get enumValues() {
        return undefined;
    }
    get isRelation() {
        return true;
    }
    get isRelationOwner() {
        return this.relationMetadata.isOwning;
    }
    get relationType() {
        var _a, _b;
        if (this.relationMetadata.isOneToOne) {
            return types_1.SimplifiedRelationType.ONE_TO_ONE;
        }
        if (this.relationMetadata.isOneToMany) {
            return types_1.SimplifiedRelationType.ONE_TO_MANY;
        }
        if ((_a = this.relationMetadata) === null || _a === void 0 ? void 0 : _a.isManyToOne) {
            return types_1.SimplifiedRelationType.MANY_TO_ONE;
        }
        if ((_b = this.relationMetadata) === null || _b === void 0 ? void 0 : _b.isManyToMany) {
            return types_1.SimplifiedRelationType.MANY_TO_MANY;
        }
    }
    get relationReferencedColumn() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.relationMetadata) === null || _a === void 0 ? void 0 : _a.joinColumns.find((jc) => { var _a; return jc.target === ((_a = this.relationMetadata) === null || _a === void 0 ? void 0 : _a.target); })) === null || _b === void 0 ? void 0 : _b.referencedColumn) === null || _c === void 0 ? void 0 : _c.propertyPath;
    }
    get relationEntity() {
        var _a;
        return (_a = this.relationMetadata.inverseEntityMetadata) === null || _a === void 0 ? void 0 : _a.target;
    }
    get isEagerRelation() {
        return this.relationMetadata.isEager;
    }
    get relationInverseSidePropertyPath() {
        return this.relationMetadata.inverseSidePropertyPath;
    }
    get isCascadeRelation() {
        return this.cascadeRelations !== undefined;
    }
    get isEmbed() {
        return !!this.relationMetadata.embeddedMetadata;
    }
    get cascadeRelations() {
        const result = {
            insert: this.relationMetadata.isCascadeInsert,
            update: this.relationMetadata.isCascadeUpdate,
            remove: this.relationMetadata.isCascadeRemove,
            softRemove: this.relationMetadata.isCascadeSoftRemove,
            recover: this.relationMetadata.isCascadeSoftRemove,
        };
        if (Object.values(result).every((flag) => !flag)) {
            return undefined;
        }
        return result;
    }
    get embeds() {
        return;
    }
}
exports.EntityRelationDescription = EntityRelationDescription;
function getTypeormEntityProperties(entity, connection) {
    if (!connection) {
        connection = (0, typeorm_1.getConnection)();
    }
    if (connection.hasMetadata(entity)) {
        const metadata = connection.getMetadata(entity);
        const ownRelations = metadata.ownRelations;
        const columnsExcludingOwnRelations = metadata.columns.filter((c) => !ownRelations.some((r) => r.joinColumns.includes(c)));
        return columnsExcludingOwnRelations
            .map((columnMetadata) => new EntityColumnDescription(columnMetadata))
            .concat(ownRelations.map((relationMetadata) => new EntityRelationDescription(relationMetadata)));
    }
    throw new Error("Can't find TypeORM metadata for " + entity);
}
exports.getTypeormEntityProperties = getTypeormEntityProperties;
function getTypeormEntity(entity, connection) {
    if (!connection) {
        connection = (0, typeorm_1.getConnection)();
    }
    if (connection.hasMetadata(entity)) {
        const metadata = connection.getMetadata(entity);
        return new EntityDescription(metadata);
    }
    throw new Error("Can't find TypeORM metadata for " + entity);
}
exports.getTypeormEntity = getTypeormEntity;
//# sourceMappingURL=typeorm-entity-metadata.js.map